// src/api/questionnaire.ts
import api from './axios';

export type AnswerValue = string | string[]; // 支持多选为数组

export interface SaveQuestionnairePayload {
  reservationId: number; // 前端用 camelCase；发送时会转成 reservation_id
  service: string;
  answers: Record<string, AnswerValue>;
}

/** 把多选数组转成 '、' 连接的字符串，用于后端不支持数组时的兜底重试 */
function normalizeAnswersToString(answers: Record<string, AnswerValue>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(answers)) {
    out[k] = Array.isArray(v) ? v.join('、') : String(v ?? '');
  }
  return out;
}

/** ✅ 检查问诊票是否已填写（后端需要 reservation_id + service） */
export const checkQuestionnaire = async (
  reservationId: number,
  service: string
): Promise<{ is_filled: boolean }> => {
  try {
    const response = await api.get('/questionnaire/check', {
      params: {
        reservation_id: reservationId, // 注意与后端一致
        service,
      },
    });
    return response.data;
  } catch (error) {
    console.error('問診票チェック失敗:', error);
    // 出错时默认未填写，避免打断流程
    return { is_filled: false };
  }
};

/** ✅ 保存问诊票（先按原样提交；如 422 再以字符串格式重试一次） */
export const saveQuestionnaire = async (
  payload: SaveQuestionnairePayload
): Promise<{ message: string }> => {
  try {
    const response = await api.post('/questionnaire/save', {
      reservation_id: payload.reservationId,
      service: payload.service,
      answers: payload.answers, // 直接发送 string | string[]
    });
    return response.data;
  } catch (error: any) {
    // 如果后端暂未接受数组，可能是 422（Pydantic 校验失败），则自动降级重试
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    const maybePydanticStringError =
      status === 422 ||
      (typeof detail === 'string' && /str|string/i.test(detail));

    if (maybePydanticStringError) {
      try {
        const response2 = await api.post('/questionnaire/save', {
          reservation_id: payload.reservationId,
          service: payload.service,
          answers: normalizeAnswersToString(payload.answers),
        });
        return response2.data;
      } catch (error2: any) {
        console.error('問診票保存失敗(降級後仍失敗):', error2);
        throw {
          detail: error2?.response?.data?.detail || '問診票の保存に失敗しました。',
          status: error2?.response?.status || 400,
        };
      }
    }

    console.error('問診票保存失敗:', error);
    throw {
      detail: detail || '問診票の保存に失敗しました。',
      status: status || 400,
    };
  }
};
